import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import ReactDOMServer from "react-dom/server";

import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { PickupPin } from "@/components/common/svgs";

/**
 * Hook for managing the pickup location marker with zoom-based scaling
 * @param {Object} options
 * @param {boolean} options.isMapReady - Whether the map is ready
 * @param {React.RefObject} options.mapRef - Reference to MarketMapBase
 * @param {Object} options.pickupLocation - Pickup location data from Redux
 * @param {string} options.mapViewMode - Current view mode ("fullMap" | "market")
 */
export const usePickupMarker = ({ isMapReady, mapRef, pickupLocation, mapViewMode }) => {
  const isDarkTheme = useIsDarkTheme();
  const pickupMarkerRef = useRef(null);
  const baseZoomRef = useRef(null);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !pickupLocation.data) return;

    const map = mapRef.current.getMap();
    if (!map) return;

    const pickupPoint = [pickupLocation.data.longitude, pickupLocation.data.latitude];

    // Remove existing marker
    if (pickupMarkerRef.current) {
      pickupMarkerRef.current.remove();
      pickupMarkerRef.current = null;
    }

    // Create marker element
    const isMarketView = mapViewMode === "market";
    const el = document.createElement("div");
    el.innerHTML = ReactDOMServer.renderToString(<PickupPin isDark={isDarkTheme} large={isMarketView} />);

    const svg = el.querySelector("svg");
    if (svg) {
      svg.style.transformOrigin = "center bottom";
      svg.style.display = "block";
    }

    const updateScale = () => {
      if (!svg || baseZoomRef.current === null) return;
      const zoom = map.getZoom();
      const zoomDiff = zoom - baseZoomRef.current;
      const scale = Math.max(0.5, 1 + zoomDiff * 0.15);
      svg.style.transform = `scale(${scale})`;
    };

    const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" }).setLngLat(pickupPoint).addTo(map);

    pickupMarkerRef.current = marker;

    const onIdle = () => {
      baseZoomRef.current = map.getZoom();
      updateScale();
      map.on("zoom", updateScale);
      map.off("idle", onIdle);
    };

    map.on("idle", onIdle);

    return () => {
      map.off("zoom", updateScale);
      map.off("idle", onIdle);
    };
  }, [isMapReady, mapViewMode, isDarkTheme, pickupLocation.data]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pickupMarkerRef.current) {
        pickupMarkerRef.current.remove();
        pickupMarkerRef.current = null;
      }
    };
  }, []);
};
